import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { useState, type ReactNode } from "react";
import { Button } from "@/core/components/ui/button";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { cn } from "@/core/utils/utils";

/**
 * A "Label (N)" toggle over a group of rows — the Needs review / Resolved
 * groups in the invoice sidebar and on the review page. Renders nothing when
 * the group is empty.
 */
export const CollapsibleGroup = ({
  label,
  count,
  defaultOpen,
  children,
}: {
  label: string;
  count: number;
  defaultOpen: boolean;
  children: ReactNode;
}) => {
  // Follow the data until the user chooses a state. Keep that choice while
  // this group is empty, since polling can add actions again.
  const [openOverride, setOpenOverride] = useState<boolean | null>(null);
  const open = openOverride ?? defaultOpen;

  if (count === 0) return null;

  return (
    <Stack gap="xs" fullWidth>
      <Button
        variant="unstyled"
        aria-expanded={open}
        onClick={() => setOpenOverride(!open)}
        className="group self-start rounded-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
      >
        <Stack direction="row" gap="xs" align="center">
          <Typography
            variant="subtitleW3"
            textColor="tertiary"
            className="group-hover:text-primary-text"
          >
            {label} ({count})
          </Typography>
          <ChevronDownIcon
            className={cn(
              "size-4 text-tertiary-text transition-transform group-hover:text-primary-text",
              {
                "rotate-180": open,
              },
            )}
          />
        </Stack>
      </Button>
      {/* Keep drafts mounted while closed; hidden content cannot receive focus. */}
      <Stack
        gap="none"
        fullWidth
        hidden={!open}
        className={cn({ hidden: !open })}
      >
        {children}
      </Stack>
    </Stack>
  );
};
